// PropertyAmazonMapView.jsx (Amazon Location version)
import React, { useState, useEffect, useRef, useMemo } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";
import styles from "./PropertyMapView.module.css";
import Listing from "../Listing/Listing";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import IconButton from "@mui/joy/IconButton";

function PropertyAmazonMapView({ listings, addressInputState }) {
  const { accessToken } = useAuth();
  const mapRef = useRef(null);
  const mapContainer = useRef(null);
  const [popupInstance, setPopupInstance] = useState(null);
  const [polygonPaths, setPolygonPaths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [boundaryError, setBoundaryError] = useState(false);
  const [mapCenter, setMapCenter] = useState([-81.379234, 28.538336]); // lng, lat
  const [userLocation, setUserLocation] = useState(null);
  const [showLocationButton, setShowLocationButton] = useState(true);

  const bounds = new maplibregl.LngLatBounds();
  const markerRefs = useRef([]);

  const region = "us-east-2";
  const colorScheme = "Light";

  const mapStyles = {
    map: `https://maps.geo.${region}.amazonaws.com/v2/styles/Standard/descriptor?key=${
      import.meta.env.VITE_AWS_LOCATION_API_KEY
    }&color-scheme=${colorScheme}`,
    satellite: `https://maps.geo.${region}.amazonaws.com/v2/styles/Satellite/descriptor?key=${
      import.meta.env.VITE_AWS_LOCATION_API_KEY
    }&color-scheme=${colorScheme}`,
  };

  const [mapStyleUrl, setMapStyleUrl] = useState(mapStyles.map);

  const formatMoney = (number) => {
    if (number >= 1_000_000) return `${(number / 1_000_000).toFixed(1)}M`;
    if (number >= 1_000) return `${(number / 1_000).toFixed(1)}K`;
    return number?.toString();
  };

  const getUserLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser.");
      return;
    }

    try {
      const permission = await navigator.permissions.query({
        name: "geolocation",
      });

      if (permission.state === "denied") {
        toast.error(
          "Location access has been denied. Please enable it in your browser settings."
        );
        return;
      }

      toast.info("Requesting your location...");
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          const userPos = [longitude, latitude];
          setUserLocation(userPos);
          setMapCenter(userPos);
          mapRef.current?.flyTo({ center: userPos, zoom: 20 });
          toast.success("Location found!");
        },
        (err) => {
          console.error("Geolocation error:", err);
          toast.error("Failed to get your location.");
        },
        { enableHighAccuracy: true }
      );
    } catch (err) {
      console.error("Permission API not supported or failed:", err);
      toast.info("Attempting to get location...");
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          const userPos = [longitude, latitude];
          setUserLocation(userPos);
          setMapCenter(userPos);
          mapRef.current?.flyTo({ center: userPos, zoom: 20 });
          toast.success("Location found!");
        },
        () => toast.error("Failed to get location"),
        { enableHighAccuracy: true }
      );
    }
  };

  const fetchCityBoundary = async (location) => {
    setLoading(true);
    setBoundaryError(false);

    try {
      let city = "",
        state = "",
        zipCode = "",
        country = "United States";

      // Extract zip code if present
      const zipMatch = location.match(/\b\d{5}(-\d{4})?\b/);
      if (zipMatch) zipCode = zipMatch[0];

      const parts = location.split(",").map((p) => p.trim());

      if (zipCode) {
        const beforeZip = parts.find((p) => p.includes(zipCode));
        if (beforeZip) {
          const tokens = beforeZip.split(" ");
          state = tokens[tokens.length - 2] || "";
          city = parts.slice(0, parts.indexOf(beforeZip)).join(", ");
        }
      } else {
        state = parts[parts.length - 2] || "";
        city = parts.slice(0, parts.length - 2).join(", ");
      }

      if (!city && parts.length >= 2) {
        city = parts[0];
        state = parts[1];
      }

      const query = new URLSearchParams({
        ...(zipCode ? { zipCode } : { city, state }),
        country,
      }).toString();

      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/boundary?${query}`
      );

      const geojson = response.data?.[0]?.geojson;
      if (!geojson || !geojson.coordinates) {
        setBoundaryError(true);
        return;
      }

      let coordinates = [];

      if (geojson.type === "Polygon") {
        if (Array.isArray(geojson.coordinates[0])) {
          coordinates = [
            geojson.coordinates[0].map(([lng, lat]) => [lng, lat]),
          ];
          setPolygonPaths(coordinates);
        } else {
          setBoundaryError(true);
          return;
        }
      } else if (geojson.type === "MultiPolygon") {
        coordinates = geojson.coordinates
          .map((polygon) => {
            if (!Array.isArray(polygon[0])) return [];
            return polygon[0].map(([lng, lat]) => [lng, lat]);
          })
          .filter((p) => p.length);
        setPolygonPaths(coordinates);
      } else if (geojson.type === "Point") {
        const [lng, lat] = geojson.coordinates;
        setPolygonPaths([]); // no polygon
        setMapCenter([lat, lng]);
      } else {
        setBoundaryError(true);
        return;
      }

      const lat = parseFloat(response.data[0].lat);
      const lon = parseFloat(response.data[0].lon);
      if (!isNaN(lat) && !isNaN(lon)) {
        setMapCenter([lon, lat]);
      } else {
        setBoundaryError(true);
      }
    } catch (err) {
      console.error("Boundary error:", err);
      setBoundaryError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (addressInputState) fetchCityBoundary(addressInputState);
  }, [addressInputState]);

  useEffect(() => {
    if (mapRef.current && mapStyleUrl) {
      mapRef.current.setStyle(mapStyleUrl);
    }
  }, [mapStyleUrl]);

  useEffect(() => {
    if (!mapContainer.current) return;

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: mapStyleUrl,
      center: [-81.379234, 28.538336], // your preferred coordinates
      zoom: 10,
    });

    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl(), "top-right");

    return () => map.remove();
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;

    markerRefs.current.forEach((marker) => marker.remove());
    markerRefs.current = [];

    if (mapRef.current.getLayer("polygon")) {
      mapRef.current.removeLayer("polygon");
    }
    if (mapRef.current.getSource("polygon")) {
      mapRef.current.removeSource("polygon");
    }

    if (!listings.length) return;

    listings.forEach((listing) => {
      const lat = parseFloat(listing.latitude);
      const lng = parseFloat(listing.longitude);

      if (isNaN(lat) || isNaN(lng)) return;

      bounds.extend([lng, lat]);

      const popup = new maplibregl.Popup({ offset: 25 }).setHTML(`
        <div style="
            width: 218px;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 10px rgba(0,0,0,0.2);
            font-family: sans-serif;
        ">
            <div style="position: relative;">
            <img src="${
              listing.image_url
            }" alt="property" style="width: 100%; height: 150px; object-fit: cover;" />
            <span style="
                position: absolute;
                top: 8px;
                left: 8px;
                background: #ff5722;
                color: white;
                font-weight: bold;
                font-size: 12px;
                padding: 2px 6px;
                border-radius: 4px;
            ">
                Pro
            </span>
            </div>
            <div style="padding: 10px;">
            <div style="font-weight: bold; font-size: 16px;">$${formatMoney(
              listing.sell_price || listing.rent_price
            )}</div>
            <div style="font-size: 14px; margin: 4px 0;">
                ${listing.bedrooms || 0} bds | ${listing.bathrooms || 0} ba | ${
        listing.sqft || 0
      } sqft
            </div>
            <div style="font-size: 12px; color: #555;">
                ${listing.address}
            </div>
            </div>
        </div>
        `);

      const marker = new maplibregl.Marker()
        .setLngLat([lng, lat])
        .addTo(mapRef.current)
        .setPopup(popup);

      markerRefs.current.push(marker);
    });

    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 15,
        duration: 1000,
      });
    }

    if (polygonPaths.length) {
      const geojson = {
        type: "FeatureCollection",
        features: polygonPaths.map((path) => ({
          type: "Feature",
          geometry: { type: "Polygon", coordinates: [path] },
        })),
      };

      if (mapRef.current.getSource("polygon")) {
        mapRef.current.getSource("polygon").setData(geojson);
      } else {
        mapRef.current.addSource("polygon", { type: "geojson", data: geojson });
        mapRef.current.addLayer({
          id: "polygon",
          type: "fill",
          source: "polygon",
          paint: {
            "fill-color": "#0096FF",
            "fill-opacity": 0.2,
          },
        });
      }
    }
  }, [listings, polygonPaths]);

  return (
    <div className={styles.propertyMapView}>
      <div className={styles.propertyList}>
        <h2 className={styles.propertyListTitle}>Properties</h2>
        <div className={styles.listingsContainer}>
          {listings.length ? (
            listings.map((l, i) => (
              <div key={i} className={styles.listingItem}>
                <Listing listing={l} handleBookmarkToggle={() => {}} />
              </div>
            ))
          ) : (
            <div className={styles.noListings}>
              <p>No properties found</p>
            </div>
          )}
        </div>
      </div>

      <div className={styles.mapWrapper}>
        {showLocationButton && (
          <IconButton
            onClick={getUserLocation}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 10,
              transform: "translateY(90px)",
            }}
          >
            <MyLocationIcon style={{ fontSize: 28, color: "#3d689c" }} />
          </IconButton>
        )}
        {loading && (
          <div className={styles.mapOverlay}>Loading city boundary...</div>
        )}
        {boundaryError && (
          <div className={styles.mapOverlay}>Error fetching city boundary</div>
        )}
        <div className={styles.mapToggleButtons}>
          <button
            className={mapStyleUrl === mapStyles.map ? styles.activeButton : ""}
            onClick={() => setMapStyleUrl(mapStyles.map)}
          >
            Map
          </button>
          <button
            className={
              mapStyleUrl === mapStyles.satellite ? styles.activeButton : ""
            }
            onClick={() => setMapStyleUrl(mapStyles.satellite)}
          >
            Satellite
          </button>
        </div>

        <div ref={mapContainer} className={styles.mapContainer} />
      </div>
    </div>
  );
}

export default PropertyAmazonMapView;
